import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import type { ProjectRole, RoleInput, RolePermission } from '../types';
import { RolePermissionsPanel } from './RolePermissionsPanel';

interface RoleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  permissions: RolePermission[];
  role?: ProjectRole | null;
  isSubmitting: boolean;
  onSubmit: (payload: RoleInput) => Promise<void>;
}

export const RoleFormDialog: React.FC<RoleFormDialogProps> = ({
  open,
  onOpenChange,
  permissions,
  role,
  isSubmitting,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [permissionIds, setPermissionIds] = useState<number[]>([]);

  const title = role ? 'Edit Role' : 'Create Role';
  const description = role
    ? 'Update role metadata and assigned permissions.'
    : 'Create a new project role and select its permissions.';

  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? '');
    setPermissionIds(role?.permissions.map((permission) => permission.id) ?? []);
  }, [open, role]);

  const isValid = useMemo(() => name.trim().length > 0, [name]);

  const togglePermission = (permissionId: number) => {
    setPermissionIds((prev) =>
      prev.includes(permissionId) ? prev.filter((id) => id !== permissionId) : [...prev, permissionId],
    );
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    await onSubmit({
      name: name.trim(),
      // The Admin/Basic distinction is managed by the backend's seeded roles
      // and shown in the roles table, so it isn't editable here. Preserve the
      // existing flags when editing; new roles are always plain roles.
      is_default: role?.is_default ?? false,
      is_admin: role?.is_admin ?? false,
      permission_ids: permissionIds,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="role-name">Role Name</Label>
            <Input
              id="role-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. QA Reviewer"
            />
          </div>

          <div className="space-y-2">
            <Label>Permissions</Label>
            <RolePermissionsPanel
              mode="edit"
              permissions={permissions}
              selectedPermissionIds={permissionIds}
              onTogglePermission={togglePermission}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Role'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
